import { inject, Injectable } from '@angular/core';
import { AlertType } from '@gains-com/ui-angular';
import { ApiService, ErrorMapType } from './api.service';
import { BehaviorSubject } from 'rxjs';
import { KycVerificationUpdateRTM } from '@gains-com/realtime';
import { KycVerification } from '@gains-com/shared';
import { KycVerificationViewService } from './view-states/kyc-verification-view.service';

@Injectable({
  providedIn: 'root',
})
export class KycService extends ApiService {
  readonly ErrorMap: ErrorMapType = {
    INTERNAL_SERVER_ERROR: {
      swallowError: false,
      title: 'Unknown Error',
      message:
        'An unknown problem occurred. Please contact customer support if the problem persists.',
      type: AlertType.Danger,
    },
    KYC_FAILED: {
      type: AlertType.Danger,
      swallowError: false,
      title: 'KYC Verification Failed',
      message: 'KYC verification failed. Please contact support.',
    },
    KYC_NOT_APPROVED: {
      type: AlertType.Danger,
      swallowError: false,
      title: 'KYC Verification Not Approved',
      message: 'KYC verification not approved. Please contact support.',
    },
    KYC_NOT_STARTED: {
      type: AlertType.Danger,
      swallowError: false,
      title: 'KYC Verification Not Approved',
      message: 'KYC verification not started. Please contact support.',
    },
  };

  private _kycVerificationViewService = inject(KycVerificationViewService);

  async getLatestKycVerification() {
    const kycStatus =
      (await this.get<KycVerification[]>('/kyc-verifications'))?.[0] ?? null;

    if (kycStatus) {
      this._kycVerificationViewService.updateKycVerification(kycStatus);
    }

    return kycStatus as KycVerification | null;
  }

  async createKycVerification(): Promise<KycVerification> {
    const kycStatus = await this.post<KycVerification>(
      '/kyc-verifications',
      {}
    );

    this._kycVerificationViewService.updateKycVerification(kycStatus);

    return kycStatus;
  }

  async syncKycVerification(verificationId: string) {
    const verif = await this.put<KycVerification>(
      `/kyc-verifications/${verificationId}`,
      {}
    );

    this._kycVerificationViewService.updateKycVerification(verif);

    return verif;
  }

  async createResumeToken(id: string) {
    return (
      (
        await this.post<{ resumeToken: string }>(
          `/kyc-verifications/${id}/resume-token`,
          {}
        )
      ).resumeToken ?? null
    );
  }

  async createOneTimeLink(id: string) {
    return (
      (
        await this.post<{ oneTimeLink: string }>(
          `/kyc-verifications/${id}/one-time-link`,
          {}
        )
      ).oneTimeLink ?? null
    );
  }
}
